import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, Search, Info, CheckCircle2 } from 'lucide-react';
import { atlasApi, AtlasUser, PermissaoUsuario, NivelAtlas } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
}

const NIVEIS: { id: NivelAtlas | ''; label: string }[] = [
  { id: '', label: 'Todos' },
  { id: 'visualizar', label: 'Visualizar' },
  { id: 'editar', label: 'Editar' },
  { id: 'rh_total', label: 'RH Total' },
];

function badgeNivel(nivel: NivelAtlas | null): { label: string; classes: string } {
  switch (nivel) {
    case 'visualizar': return { label: 'Visualizar', classes: 'bg-blue-100 text-blue-700' };
    case 'editar': return { label: 'Editar', classes: 'bg-purple-100 text-purple-700' };
    case 'rh_total': return { label: 'RH Total', classes: 'bg-orange-100 text-orange-700' };
    default: return { label: 'Sem acesso', classes: 'bg-gray-100 text-gray-500' };
  }
}

const Permissoes: React.FC<Props> = ({ user }) => {
  const [itens, setItens] = useState<PermissaoUsuario[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [buscaDebounce, setBuscaDebounce] = useState('');
  const [filtroNivel, setFiltroNivel] = useState<NivelAtlas | ''>('');

  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [salvoId, setSalvoId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounce(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.listarPermissoes(user, {
      busca: buscaDebounce || undefined,
      nivel: filtroNivel || undefined,
    })
      .then(r => { if (!cancel) { setItens(r.itens || []); setTotal(r.total || 0); } })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, buscaDebounce, filtroNivel]);

  const alterarNivel = async (alvo: PermissaoUsuario, novo: NivelAtlas | null) => {
    setSalvandoId(alvo._id);
    setSalvoId(null);
    setErro(null);
    try {
      await atlasApi.atualizarPermissao(user, alvo._id, novo);
      setItens(prev => prev.map(i => i._id === alvo._id ? { ...i, nivel_atlas: novo } : i));
      setSalvoId(alvo._id);
      setTimeout(() => setSalvoId(s => (s === alvo._id ? null : s)), 2000);
    } catch (e: any) {
      setErro(e.message || 'Erro ao salvar permissão.');
    } finally {
      setSalvandoId(s => (s === alvo._id ? null : s));
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Permissões do Atlas</h2>
          <p className="text-sm text-gray-500">Defina quem pode ver e editar dados de pessoas.</p>
        </div>
        <span className="text-xs text-gray-400">{total} usuário{total === 1 ? '' : 's'}</span>
      </div>

      {/* Nota */}
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        Após alterar o nível, o usuário precisa <strong>deslogar e logar de novo</strong> para que a sessão dele leia a nova permissão.
      </div>

      {/* Busca + filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={busca}
            onChange={ev => setBusca(ev.target.value)}
            placeholder="Buscar por nome ou e-mail..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {NIVEIS.map(n => (
            <button
              key={n.id || 'todos'}
              onClick={() => setFiltroNivel(n.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                filtroNivel === n.id
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'
              }`}
            >
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {erro && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>
      )}

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading && itens.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Carregando usuários...
          </div>
        ) : itens.length === 0 ? (
          <div className="p-12 text-center text-gray-400">Nenhum usuário encontrado.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Usuário</th>
                <th className="px-4 py-2 text-left">Nível atual</th>
                <th className="px-4 py-2 text-left">Alterar nível</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {itens.map(u => {
                const b = badgeNivel(u.nivel_atlas);
                return (
                  <tr key={u._id} className="hover:bg-purple-50/30 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <AvatarColaborador nome={u.nome || u.email} size={36} />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                            {u.nome || u.email}
                            {u.is_admin && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-700">admin</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.classes}`}>{b.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={u.nivel_atlas || ''}
                          disabled={salvandoId === u._id}
                          onChange={ev => alterarNivel(u, (ev.target.value || null) as NivelAtlas | null)}
                          className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 disabled:opacity-50"
                        >
                          <option value="">— Sem acesso</option>
                          <option value="visualizar">Visualizar</option>
                          <option value="editar">Editar</option>
                          <option value="rh_total">RH Total</option>
                        </select>
                        {salvandoId === u._id && <Loader2 className="w-4 h-4 animate-spin text-purple-500" />}
                        {salvoId === u._id && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="w-4 h-4" /> Atualizado
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Permissoes;
